import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, ShieldCheck, Store, Clock, Ban, CreditCard as CardIcon, LogOut } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/AppHeader";
import StoresTable, { storeState } from "@/components/admin/StoresTable";
import { CreateStoreDialog, PlanDialog, PasswordDialog } from "@/components/admin/StoreDialogs";
import SignupModeCard from "@/components/admin/SignupModeCard";
import ActivityPanel from "@/components/admin/ActivityPanel";
import PlansPanel from "@/components/admin/PlansPanel";
import PaymentsPanel from "@/components/admin/PaymentsPanel";
import AccountDialog from "@/components/admin/AccountDialog";

const FILTERS = [["all", "All"], ["pending", "Pending"], ["active", "Active"], ["blocked", "Blocked"], ["expired", "Expired"]];

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-5 py-4" data-testid={testId}>
    <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Icon size={12} className={tone} /> {label}</p>
    <p className="font-heading text-3xl font-extrabold mt-1">{value}</p>
  </div>
);

export default function SuperAdmin() {
  const { user, logout, impersonate } = useAuth();
  const [tab, setTab] = useState("stores");
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/admin/plans").then((r) => setPlans(r.data)); }, []);
  const openStore = async (s) => {
    try { const { data } = await api.post(`/admin/stores/${s.id}/impersonate`); impersonate(data.token); } catch (e) { toast.error(errMsg(e)); }
  };
  const [stores, setStores] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [planFor, setPlanFor] = useState(null);
  const [pwFor, setPwFor] = useState(null);

  const load = useCallback(() => api.get("/admin/stores").then((r) => setStores(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const replace = (row) => setStores((list) => list.map((s) => (s.id === row.id ? row : s)));
  const patch = async (s, body, msg) => {
    try { replace((await api.patch(`/admin/stores/${s.id}`, body)).data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); }
  };

  const counts = useMemo(() => {
    const c = { all: 0, pending: 0, active: 0, blocked: 0, expired: 0, cards: 0 };
    (stores || []).forEach((s) => { c.all++; c[storeState(s)]++; c.cards += s.cards; });
    return c;
  }, [stores]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (stores || []).filter((x) => (filter === "all" || storeState(x) === filter)
      && (!s || [x.email, x.name, x.store_name, x.city].some((v) => (v || "").toLowerCase().includes(s))));
  }, [stores, filter, q]);

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="h-14 sticky top-0 z-40 bg-slate-950/90 backdrop-blur-md border-b border-slate-800 px-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Brand />
          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1" data-testid="superadmin-badge"><ShieldCheck size={11} /> Super admin</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 font-mono hidden sm:block" data-testid="superadmin-email">{user?.email}</span>
          <AccountDialog email={user?.email} />
          <button onClick={logout} data-testid="logout-button" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-md border border-slate-800 hover:border-slate-600 transition-colors"><LogOut size={14} /> Logout</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4 rise">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-amber-400">Multi-tenant control</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Stores</h1>
            <p className="text-sm text-slate-400 mt-2">Approve, block and manage every jewellery store using the studio.</p>
          </div>
          <button className="cf-btn-primary h-10 px-4 text-sm" onClick={() => setCreating(true)} data-testid="create-store-button"><Plus size={15} /> New store</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 rise" style={{ animationDelay: "60ms" }}>
          <Stat icon={Store} label="Total stores" value={counts.all} tone="text-amber-400" testId="stat-total-stores" />
          <Stat icon={Clock} label="Awaiting approval" value={counts.pending} tone="text-sky-400" testId="stat-pending" />
          <Stat icon={Ban} label="Blocked / expired" value={counts.blocked + counts.expired} tone="text-red-400" testId="stat-blocked" />
          <Stat icon={CardIcon} label="Cards saved" value={counts.cards} tone="text-emerald-400" testId="stat-cards" />
        </div>

        <div className="flex gap-1 border-b border-slate-800" data-testid="admin-tabs">
          {[["stores", "Stores"], ["activity", "Activity"], ["plans", "Plan packages"], ["payments", "Payments"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-amber-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{label}</button>
          ))}
        </div>

        {tab === "activity" && <ActivityPanel />}
        {tab === "plans" && <PlansPanel plans={plans} setPlans={setPlans} />}
        {tab === "payments" && <PaymentsPanel />}
        {tab === "stores" && <>
        <SignupModeCard />

        <section className="rounded-xl border border-slate-800 bg-slate-900/60">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-800">
            <div className="flex flex-wrap gap-1" data-testid="store-filters">
              {FILTERS.map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`store-filter-${k}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${filter === k ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"}`}>
                  {label} <span className="font-mono text-slate-500">{counts[k]}</span>
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input className="cf-input h-8 text-xs w-64" style={{ paddingLeft: 30 }} placeholder="Store, email or city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="store-search-input" />
            </div>
          </div>
          <StoresTable key={`${filter}|${q}`} stores={shown} loading={!stores}
            onApprove={async (s) => {
              if (!s.requested_plan_id) return patch(s, { status: "active" }, `${s.store_name || s.email} approved`);
              try {
                replace((await api.post(`/admin/stores/${s.id}/apply-plan`, { plan_id: s.requested_plan_id })).data);
                toast.success(`${s.store_name || s.email} approved on ${s.requested_plan_name}`);
              } catch (e) { toast.error(errMsg(e)); }
            }}
            onBlock={(s) => patch(s, { status: "blocked" }, `${s.store_name || s.email} blocked`)}
            onUnblock={(s) => patch(s, { status: "active" }, `${s.store_name || s.email} unblocked`)}
            onPlan={setPlanFor} onPassword={setPwFor} onOpen={openStore} />
        </section>
        </>}
      </main>

      <CreateStoreDialog plans={plans} open={creating} onClose={() => setCreating(false)} onCreated={(row) => setStores((l) => [row, ...(l || [])])} />
      <PlanDialog plans={plans} store={planFor} onClose={() => setPlanFor(null)} onSaved={replace} />
      <PasswordDialog store={pwFor} onClose={() => setPwFor(null)} />
    </div>
  );
}
